import { Inject, Injectable, InjectionToken } from '@angular/core';

interface OAuthAuthorizationLoginWindow {
	readonly sessionStorage: Storage;
	readonly location: Pick<Location, 'replace'>;
}

const OAUTH_AUTHORIZATION_TRANSACTION_PATTERN = /^oauth_tx_[A-Za-z0-9_-]{43}$/;
const OAUTH_AUTHORIZATION_RETURN_PATH_PATTERN = /^\/oauth\/authorize\/(oauth_tx_[A-Za-z0-9_-]{43})$/;
const OAUTH_AUTHORIZATION_RETURN_PATH_STORAGE_KEY = 'cce.oauth.authorization.return-path';
// Keep the browser hand-off for the API's maximum configurable transaction TTL.
// The API remains authoritative for the actual transaction expiry.
const OAUTH_AUTHORIZATION_RETURN_PATH_TTL_MS = 30 * 60 * 1000;

interface StoredOAuthAuthorizationReturnPath {
	readonly returnPath: string;
	readonly storedAt: number;
}

export const OAUTH_AUTHORIZATION_LOGIN_WINDOW = new InjectionToken<OAuthAuthorizationLoginWindow>(
	'OAuth authorization login browser window',
	{
		providedIn: 'root',
		factory: () => window
	}
);

export const OAUTH_AUTHORIZATION_LOGIN_NOW = new InjectionToken<() => number>('OAuth authorization login clock', {
	providedIn: 'root',
	factory: () => Date.now
});

export function isValidOAuthAuthorizationTransaction(transaction: string | null | undefined): transaction is string {
	return typeof transaction === 'string' && OAUTH_AUTHORIZATION_TRANSACTION_PATTERN.test(transaction);
}

export function normalizeOAuthAuthorizationReturnPath(value: unknown): string | undefined {
	if (typeof value !== 'string') {
		return undefined;
	}

	const match = OAUTH_AUTHORIZATION_RETURN_PATH_PATTERN.exec(value);
	return match && isValidOAuthAuthorizationTransaction(match[1]) ? `/oauth/authorize/${match[1]}` : undefined;
}

export function normalizeSameOriginAppPath(value: unknown): string | undefined {
	if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) {
		return undefined;
	}

	try {
		const base = 'https://oauth-local.invalid';
		const url = new URL(value, base);
		return url.origin === base ? `${url.pathname}${url.search}${url.hash}` : undefined;
	} catch {
		return undefined;
	}
}

export function oauthAuthorizationReturnPathAfterLogin(returnPath: unknown, postLoginRedirect: unknown): string | undefined {
	return postLoginRedirect ? undefined : normalizeOAuthAuthorizationReturnPath(returnPath);
}

function parseStoredReturnPath(value: string | null, now: number): string | undefined {
	if (!value) {
		return undefined;
	}

	let storedValue: unknown;
	try {
		storedValue = JSON.parse(value);
	} catch {
		return undefined;
	}

	if (!storedValue || typeof storedValue !== 'object') {
		return undefined;
	}

	const stored = storedValue as Partial<StoredOAuthAuthorizationReturnPath>;
	if (
		typeof stored.storedAt !== 'number' ||
		!Number.isFinite(stored.storedAt) ||
		stored.storedAt > now ||
		now - stored.storedAt > OAUTH_AUTHORIZATION_RETURN_PATH_TTL_MS
	) {
		return undefined;
	}

	return normalizeOAuthAuthorizationReturnPath(stored.returnPath);
}

/**
 * Preserves the one safe return path that needs to survive a full-page SAML
 * round trip. The value is scoped to the current browser tab, consumed once,
 * and validated both before writing and after reading.
 */
@Injectable({ providedIn: 'root' })
export class OAuthAuthorizationLoginResumeService {
	constructor(
		@Inject(OAUTH_AUTHORIZATION_LOGIN_WINDOW) private readonly browserWindow: OAuthAuthorizationLoginWindow,
		@Inject(OAUTH_AUTHORIZATION_LOGIN_NOW) private readonly now: () => number
	) {}

	remember(returnPath: unknown): void {
		const normalizedPath = normalizeOAuthAuthorizationReturnPath(returnPath);

		try {
			// A new SAML attempt must also clear any stale authorization request.
			this.browserWindow.sessionStorage.removeItem(OAUTH_AUTHORIZATION_RETURN_PATH_STORAGE_KEY);
			if (normalizedPath) {
				const storedValue: StoredOAuthAuthorizationReturnPath = {
					returnPath: normalizedPath,
					storedAt: this.now()
				};
				this.browserWindow.sessionStorage.setItem(OAUTH_AUTHORIZATION_RETURN_PATH_STORAGE_KEY, JSON.stringify(storedValue));
			}
		} catch {
			// SAML login remains usable when browser storage is unavailable.
		}
	}

	take(): string | undefined {
		try {
			const storedValue = this.browserWindow.sessionStorage.getItem(OAUTH_AUTHORIZATION_RETURN_PATH_STORAGE_KEY);
			this.browserWindow.sessionStorage.removeItem(OAUTH_AUTHORIZATION_RETURN_PATH_STORAGE_KEY);
			return parseStoredReturnPath(storedValue, this.now());
		} catch {
			return undefined;
		}
	}

	resume(returnPath: unknown): boolean {
		const normalizedPath = normalizeOAuthAuthorizationReturnPath(returnPath);
		if (!normalizedPath) {
			return false;
		}

		this.browserWindow.location.replace(normalizedPath);
		return true;
	}

	reloadLocalPath(returnPath: unknown): boolean {
		const normalizedPath = normalizeSameOriginAppPath(returnPath);
		if (!normalizedPath) {
			return false;
		}

		this.browserWindow.location.replace(normalizedPath);
		return true;
	}
}
